/*
Floating debug overlay for on-device smoke testing.

Intercepts console.log/warn/error at module install time and renders
the captured lines in a fixed bottom panel so we can read the runtime
behaviour on devices that lack accessible DevTools (Vanadium standalone
PWA, iOS Safari home-screen apps).

Activation: only installs when window.location.hash contains 'debug'
(e.g. `…/#debug`, `…/#/map?debug=1`). Otherwise the module is a no-op.

Outil de smoke pérenne : seul accès aux logs runtime sur les devices
sans DevTools. Side effect utile : `#debug` bypasse aussi l'onboarding
(cf. main.ts).
*/

const kMaxLines: number = 200  // ring buffer cap — older lines drop off the top once exceeded
const kHash: string = 'debug'  // substring searched in window.location.hash

interface LogLine
{
	at: number              // performance.now() relative timestamp
	level: 'log' | 'warn' | 'error'
	text: string
}

let fLines: LogLine[] = []  // ring buffer of captured console messages
let fOverlay: HTMLElement | null = null
let fBody: HTMLElement | null = null
let fInstalled: boolean = false

/*
Install once at boot. Subsequent calls are no-ops. The activation gate
checks the URL hash so the overlay never appears in normal use.
*/
export function maybeInstallDebugOverlay(): void
{
	if (fInstalled) return
	if (typeof window === 'undefined' || typeof document === 'undefined') return
	const vHash = window.location.hash
	if (!vHash || !vHash.toLowerCase().includes(kHash)) return
	fInstalled = true

	patchConsole()
	mountOverlay()
}

function patchConsole(): void
{
	const vOrig = {
		log: console.log.bind(console),
		warn: console.warn.bind(console),
		error: console.error.bind(console),
	}
	const vCapture = (inLevel: 'log' | 'warn' | 'error') => (...inArgs: unknown[]): void => {
		try { record(inLevel, inArgs) } catch { /* never break the original console */ }
		vOrig[inLevel](...inArgs)
	}
	console.log = vCapture('log')
	console.warn = vCapture('warn')
	console.error = vCapture('error')
}

function record(inLevel: 'log' | 'warn' | 'error', inArgs: unknown[]): void
{
	const vText = inArgs.map(stringifyArg).join(' ')
	// Drop MapLibre's benign sprite-missing flood ("Image "X" could not be
	// loaded…") — the OpenFreeMap Liberty style references POI sprites we
	// don't ship, firing ~50 identical warnings on every map load that bury
	// the signal and churn the 200-line ring. Overlay-only filter; the real
	// console (and `vOrig`) still gets them.
	if (vText.includes('could not be loaded. Please make sure you have added the image')) return
	fLines.push({ at: performance.now(), level: inLevel, text: vText })
	if (fLines.length > kMaxLines) fLines.splice(0, fLines.length - kMaxLines)
	if (fBody) appendLine(fBody, fLines[fLines.length - 1])
}

function stringifyArg(inArg: unknown): string
{
	if (typeof inArg === 'string') return inArg
	if (inArg instanceof Error) return `${inArg.name}: ${inArg.message}${inArg.stack ? '\n' + inArg.stack : ''}`
	try { return JSON.stringify(inArg) }
	catch { return String(inArg) }
}

function mountOverlay(): void
{
	const vEl = document.createElement('div')
	vEl.id = 'debug-overlay'
	vEl.style.cssText = [
		'position:fixed',
		'left:0',
		'right:0',
		'bottom:0',
		'max-height:45vh',
		'background:rgba(0,0,0,0.88)',
		'color:#0f0',
		'font:11px/1.35 ui-monospace,Menlo,Consolas,monospace',
		'z-index:99999',
		'display:flex',
		'flex-direction:column',
		'border-top:1px solid #444',
	].join(';')

	const vHeader = document.createElement('div')
	vHeader.style.cssText = 'display:flex;gap:6px;padding:4px 6px;background:#111;border-bottom:1px solid #333;color:#aaa'
	vHeader.innerHTML = `
		<span style="flex:1;font-weight:600">DEBUG (${fLines.length})</span>
		<button type="button" data-act="copy" style="background:#222;color:#0f0;border:1px solid #333;padding:2px 8px;border-radius:3px">📋</button>
		<button type="button" data-act="clear" style="background:#222;color:#ff0;border:1px solid #333;padding:2px 8px;border-radius:3px">🗑️</button>
		<button type="button" data-act="toggle" style="background:#222;color:#fff;border:1px solid #333;padding:2px 8px;border-radius:3px">▼</button>
	`

	const vBody = document.createElement('div')
	vBody.style.cssText = 'flex:1;overflow-y:auto;padding:6px 8px;white-space:pre-wrap;word-break:break-word'

	vEl.appendChild(vHeader)
	vEl.appendChild(vBody)
	document.body.appendChild(vEl)

	fOverlay = vEl
	fBody = vBody

	for (const vLine of fLines) appendLine(vBody, vLine)

	vHeader.addEventListener('click', (inEv) => {
		const vBtn = (inEv.target as HTMLElement)?.closest('button[data-act]') as HTMLButtonElement | null
		if (!vBtn) return
		const vAct = vBtn.dataset.act
		if (vAct === 'copy') void copyAll(vBtn)
		else if (vAct === 'clear') clearAll(vHeader)
		else if (vAct === 'toggle') toggleVisibility(vBody, vBtn)
	})
}

function appendLine(inBody: HTMLElement, inLine: LogLine): void
{
	const vEl = document.createElement('div')
	const vColor = inLine.level === 'error' ? '#f66' : inLine.level === 'warn' ? '#ff0' : '#0f0'
	vEl.style.cssText = `color:${vColor};margin:0 0 2px`
	const vTs = new Date().toISOString().slice(11, 23)  // HH:MM:SS.mmm UTC
	vEl.textContent = `${vTs} ${inLine.level === 'log' ? '' : `[${inLine.level}] `}${inLine.text}`
	inBody.appendChild(vEl)
	// Auto-scroll to bottom if the user wasn't reading the middle
	if (inBody.scrollHeight - inBody.scrollTop - inBody.clientHeight < 80)
	{
		inBody.scrollTop = inBody.scrollHeight
	}
	const vHeader = inBody.previousElementSibling as HTMLElement | null
	const vCounter = vHeader?.querySelector('span')
	if (vCounter) vCounter.textContent = `DEBUG (${fLines.length})`
}

async function copyAll(inBtn: HTMLButtonElement): Promise<void>
{
	// Copy the whole rendered body — includes the frozen "previous session"
	// block (pre-reload lines) plus the live log, which is exactly the
	// sequence we need across a reload. Falls back to the live ring if the
	// body isn't mounted.
	const vText = fBody
		? fBody.innerText
		: fLines.map((inL) => {
			const vTs = new Date().toISOString().slice(11, 23)
			return `${vTs} [${inL.level}] ${inL.text}`
		}).join('\n')
	try
	{
		await navigator.clipboard.writeText(vText)
		flashButton(inBtn, '✓')
	}
	catch
	{
		// Fallback: select-and-copy via a temp textarea (older Vanadium).
		const vTa = document.createElement('textarea')
		vTa.value = vText
		document.body.appendChild(vTa)
		vTa.select()
		try { document.execCommand('copy') } catch { /* give up silently */ }
		document.body.removeChild(vTa)
		flashButton(inBtn, '✓')
	}
}

function clearAll(inHeader: HTMLElement): void
{
	fLines = []
	if (fBody) fBody.innerHTML = ''
	const vCounter = inHeader.querySelector('span')
	if (vCounter) vCounter.textContent = 'DEBUG (0)'
}

function toggleVisibility(inBody: HTMLElement, inBtn: HTMLButtonElement): void
{
	const vHidden = inBody.style.display === 'none'
	inBody.style.display = vHidden ? 'block' : 'none'
	inBtn.textContent = vHidden ? '▼' : '▲'
	if (fOverlay) fOverlay.style.maxHeight = vHidden ? '45vh' : 'auto'
}

function flashButton(inBtn: HTMLButtonElement, inMark: string): void
{
	const vPrev = inBtn.textContent
	inBtn.textContent = inMark
	window.setTimeout(() => { inBtn.textContent = vPrev }, 800)
}
